import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, extractErrorMessage } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { DeadlineBadge } from "@/components/DeadlineBadge";
import { QaBadge } from "@/components/QaBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { RoutingRecipientPreview } from "@/components/admin/RoutingRecipientPreview";
import { toast } from "sonner";

const HOLD_REASONS = {
  surrounding_area: { label: "Surrounding area", cls: "bg-amber-100 text-amber-800" },
  quote: { label: "Quote", cls: "bg-indigo-100 text-indigo-800" },
};
const holdReasonMeta = (r) => HOLD_REASONS[r] || { label: "New client review", cls: "bg-slate-100 text-slate-700" };

const STAGE_FILTERS = [
  { v: "all", l: "All stages" },
  { v: "awaiting_approval", l: "Awaiting ARPS Approval" },
  { v: "urgent_service", l: "Urgent / Rush Service · Awaiting Approval" },
  { v: "pending_review", l: "Service Completion · Awaiting Review" },
  { v: "awaiting_assignment", l: "Awaiting Assignment" },
  { v: "paid_awaiting_assignment", l: "Paid · Awaiting Assignment" },
  { v: "paid_pending_review", l: "Paid · Pending ARPS Review" },
  { v: "assigned", l: "Assigned · no verified activity" },
  { v: "active", l: "Active · verified activity" },
  { v: "active_work", l: "Current active work" },
  { v: "completed_today", l: "Completed Today" },
  { v: "completed", l: "Completed" },
];

const RISK_FILTERS = [
  { v: "all", l: "Any deadline" },
  { v: "at_risk", l: "At risk (all)" },
  { v: "passed", l: "Deadline Passed" },
  { v: "urgent", l: "Urgent" },
  { v: "approaching", l: "Approaching" },
  { v: "on_track", l: "On Track" },
];

const PAGE_SIZE = 100;

export default function AdminJobs() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const stage = params.get("stage") || "all";
  const risk = params.get("risk") || "all";
  const reassigned = params.get("reassigned") || "all";
  const verify = params.get("verify") || "all";
  const sort = params.get("sort") || "desc";
  const q = params.get("q") || "";
  const page = parseInt(params.get("page") || "1", 10);
  const [search, setSearch] = useState(q);
  const [approveTarget, setApproveTarget] = useState(null);

  const setParam = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v == null || v === "all") next.delete(k); else next.set(k, v);
    });
    if (!("page" in patch)) next.set("page", "1");
    setParams(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/jobs", { params: { stage, risk, reassigned, verify, sort, q, page, page_size: PAGE_SIZE } });
      setData(data);
    } catch { setData({ items: [], total: 0, page: 1, pages: 1 }); }
    finally { setLoading(false); }
  }, [stage, risk, reassigned, verify, sort, q, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSearch(q); }, [q]);

  const stageLabel = STAGE_FILTERS.find((f) => f.v === stage)?.l || "All stages";

  const approveForPayment = async () => {
    const o = approveTarget;
    if (!o) return;
    setApproveTarget(null);
    try {
      await api.post(`/admin/orders/${o.id}/approve-for-payment`);
      toast.success(`#${o.job_number} approved — client notified to pay.`);
      load();
    } catch (err) { toast.error(extractErrorMessage(err, "Could not approve order.")); }
  };

  const approveReview = async (e, o) => {
    e.stopPropagation();
    try { await api.post(`/admin/orders/${o.id}/approve-review`); toast.success(`#${o.job_number} approved — released to the assignment pool.`); load(); }
    catch (err) { toast.error(extractErrorMessage(err, "Could not approve order.")); }
  };
  const supplementalCharge = async (e, o) => {
    e.stopPropagation();
    const val = window.prompt(`Verified TOTAL page count for #${o.job_number} (issues a supplemental charge for any pages beyond what was already charged):`);
    if (val == null) return;
    const n = parseInt(val, 10);
    if (!Number.isInteger(n) || n < 0) { toast.error("Enter a whole number of pages."); return; }
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/supplemental-charge`, { corrected_total_pages: n });
      toast.success(data.supplemental ? `Supplemental invoice ${data.invoice_number} for $${Number(data.delta).toFixed(2)} issued.` : "Verified — no additional charge due.");
      load();
    } catch (err) { toast.error(extractErrorMessage(err, "Could not issue supplemental charge.")); }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        {stage === "awaiting_approval" ? (
          <>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900" data-testid="jobs-review-title">Hold Queue — Awaiting ARPS Approval</h2>
            <p className="text-slate-500">Count: <span className="font-semibold text-amber-700" data-testid="jobs-review-count">{data.total.toLocaleString()}</span> · orders held for ARPS review before the client can pay.</p>
          </>
        ) : (
          <>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">All service jobs</h2>
            <p className="text-slate-500">Every acknowledged order is accountable and reachable here.</p>
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <form
          className="relative flex-1 min-w-56"
          onSubmit={(e) => { e.preventDefault(); setParam({ q: search }); }}
        >
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="jobs-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search job #, defendant, client, server… (Enter)" className="pl-9" />
        </form>
        <Select value={stage} onValueChange={(v) => setParam({ stage: v })}>
          <SelectTrigger data-testid="job-status-filter-select" className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>{STAGE_FILTERS.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={risk} onValueChange={(v) => setParam({ risk: v })}>
          <SelectTrigger data-testid="job-deadline-filter-select" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>{RISK_FILTERS.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={reassigned} onValueChange={(v) => setParam({ reassigned: v })}>
          <SelectTrigger data-testid="job-reassigned-filter-select" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any reassignment</SelectItem>
            <SelectItem value="yes">Reassigned</SelectItem>
            <SelectItem value="no">Never reassigned</SelectItem>
          </SelectContent>
        </Select>
        <Select value={verify} onValueChange={(v) => setParam({ verify: v })}>
          <SelectTrigger data-testid="job-verify-filter-select" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any ARPS Verify</SelectItem>
            <SelectItem value="not_verified">GPS not verified</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="jobs-sort-toggle" variant="outline" onClick={() => setParam({ sort: sort === "desc" ? "asc" : "desc" })}>
          {sort === "desc" ? "Newest → Oldest" : "Oldest → Newest"}
        </Button>
        {(stage !== "all" || risk !== "all" || reassigned !== "all" || q || sort !== "desc") && (
          <Button data-testid="jobs-clear-all" variant="ghost" onClick={() => { setSearch(""); setParams(new URLSearchParams()); }}>Clear all</Button>
        )}
      </div>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span data-testid="jobs-total-count">
          <span className="font-semibold text-slate-900">{data.total.toLocaleString()}</span> job(s) · {stageLabel}
          {risk !== "all" && ` · ${RISK_FILTERS.find((f) => f.v === risk)?.l}`}
          {q && ` · “${q}”`}
        </span>
        <span>Page {data.page} of {data.pages}</span>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3">Job #</th><th className="px-5 py-3">Defendant</th>
              <th className="px-5 py-3">Client</th><th className="px-5 py-3">Business</th><th className="px-5 py-3">Server</th>
              {stage === "awaiting_approval" && <><th className="px-5 py-3">Client charge</th><th className="px-5 py-3">Server comp</th><th className="px-5 py-3">Hold reason</th></>}
              <th className="px-5 py-3">Attempts</th><th className="px-5 py-3">Deadline</th><th className="px-5 py-3">QA</th><th className="px-5 py-3">Stage</th>
              {stage === "awaiting_approval" && <th className="px-5 py-3">Action</th>}
              {stage === "paid_pending_review" && <th className="px-5 py-3">ARPS Review</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.items.map((o) => (
              <tr key={o.id} data-testid={`admin-jobs-row-${o.job_number}`} onClick={() => nav(`/admin/jobs/${o.id}`)} className="cursor-pointer transition hover:bg-slate-50">
                <td className="px-5 py-3 font-mono-arps font-semibold text-slate-900">#{o.job_number}</td>
                <td className="px-5 py-3 text-slate-700">{o.defendant}</td>
                <td className="px-5 py-3 text-slate-500">{o.client_name}</td>
                <td className="px-5 py-3 text-slate-500">{o.agency_name || "—"}</td>
                <td className="px-5 py-3 text-slate-500">{o.assigned_server_name || <span className="text-amber-600">Unassigned</span>}</td>
                {stage === "awaiting_approval" && <>
                  <td className="px-5 py-3 font-semibold text-slate-700" data-testid={`job-client-charge-${o.job_number}`}>{o.client_charge != null ? `$${Number(o.client_charge).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—"}</td>
                  <td className="px-5 py-3 font-semibold text-emerald-700" data-testid={`job-server-comp-${o.job_number}`}>{o.server_compensation != null ? `$${Number(o.server_compensation).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—"}</td>
                  <td className="px-5 py-3" data-testid={`job-hold-reason-${o.job_number}`}>
                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${holdReasonMeta(o.hold_reason).cls}`}>{holdReasonMeta(o.hold_reason).label}</span>
                  </td>
                </>}
                <td className="px-5 py-3 text-slate-500">{o.attempts_count ?? 0}</td>
                <td className="px-5 py-3">{o.pipeline_stage !== "completed" ? <DeadlineBadge status={o.deadline_status} days={o.days_to_deadline} /> : <span className="text-xs text-slate-400">—</span>}</td>
                <td className="px-5 py-3">{o.qa ? <QaBadge status={o.qa.qa_status} testid={`qa-badge-${o.job_number}`} /> : <span className="text-xs text-slate-400">—</span>}</td>
                <td className="px-5 py-3"><StatusBadge status={o.status} />{o.has_unverified_attempt && <span data-testid={`job-unverified-flag-${o.job_number}`} title="Has an Unverified / photo-not-live attempt awaiting admin override" className="ml-1.5 inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">Unverified</span>}</td>
                {stage === "awaiting_approval" && (
                  <td className="px-5 py-3 whitespace-nowrap">
                    {o.hold_reason === "quote" && !o.quote_priced ? (
                      <span data-testid={`needs-pricing-${o.job_number}`} className="text-xs font-semibold text-indigo-700">Needs pricing first</span>
                    ) : (
                      <Button size="sm" data-testid={`approve-for-payment-${o.job_number}`} onClick={(e) => { e.stopPropagation(); setApproveTarget(o); }} className="bg-emerald-600 hover:bg-emerald-700">Approve for payment</Button>
                    )}
                  </td>
                )}
                {stage === "paid_pending_review" && (
                  <td className="px-5 py-3 whitespace-nowrap">
                    <Button size="sm" data-testid={`approve-review-${o.job_number}`} onClick={(e) => approveReview(e, o)} className="mr-2 bg-emerald-600 hover:bg-emerald-700">Approve</Button>
                    <span className="mr-2 inline-block align-middle"><RoutingRecipientPreview orderId={o.id} jobNumber={o.job_number} /></span>
                    <Button size="sm" variant="outline" data-testid={`supplemental-${o.job_number}`} onClick={(e) => supplementalCharge(e, o)}>Supplemental</Button>
                  </td>
                )}
              </tr>
            ))}
            {!loading && data.items.length === 0 && <tr><td colSpan={stage === "awaiting_approval" ? 13 : stage === "paid_pending_review" ? 10 : 9} className="px-5 py-12 text-center text-slate-400">No jobs match this filter.</td></tr>}
            {loading && <tr><td colSpan={stage === "awaiting_approval" ? 13 : stage === "paid_pending_review" ? 10 : 9} className="px-5 py-12 text-center text-slate-400">Loading…</td></tr>}
          </tbody>
        </table>
      </div>

      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button data-testid="jobs-prev-page" variant="outline" size="sm" disabled={data.page <= 1} onClick={() => setParam({ page: String(data.page - 1) })}>
            <ChevronLeft className="mr-1 h-4 w-4" /> Prev
          </Button>
          <span className="text-sm text-slate-500">Page {data.page} / {data.pages}</span>
          <Button data-testid="jobs-next-page" variant="outline" size="sm" disabled={data.page >= data.pages} onClick={() => setParam({ page: String(data.page + 1) })}>
            Next <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      )}

      <AlertDialog open={!!approveTarget} onOpenChange={(o) => { if (!o) setApproveTarget(null); }}>
        <AlertDialogContent data-testid="approve-for-payment-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Approve for payment?</AlertDialogTitle>
            <AlertDialogDescription>
              This notifies the client to pay{approveTarget ? ` for order #${approveTarget.job_number}` : ""}. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="approve-for-payment-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="approve-for-payment-confirm" onClick={approveForPayment} className="bg-emerald-600 hover:bg-emerald-700">Approve &amp; notify</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
